import React, {useEffect, useState, useMemo} from 'react';
import {Typography, Checkbox, Box, Tooltip} from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {Search as SearchIcon} from '@mui/icons-material';
import {ArrowRight} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useTrovoConfig} from 'src/utils/config';
import SampleRow from '../rna/steps/step1/SampleRow';
import {DefaultChIPSampleGroup, expandSampleSelection} from 'src/utils/chip';
import {
    ActionBar,
    ActionItemStyled,
    ActionTitleStyled,
    ActionTextStyled,
    SelectedRowsTextStyled,
    FiltersContainer,
    FiltersWrapper,
    Search,
    SearchIconWrapper,
    StyledInputBase
} from '../rna/steps/step1/ExpandableTable.styles';
import {isDemoExperiment} from 'src/utils/demo-data';
import SampleGroupSelector from './SampleGroupSelector';

const DBA_PEAK_THRESHOLD = 1000;

type SamplesTableProps = {
    data: any[];
    displayedRows: any[];
    selectedSamples: string[];
    handleSelectAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSelectRow: (id: string) => void;
    /** When true, shows an extra peaks column and disables selection of samples with peaks < DBA_PEAK_THRESHOLD */
    filterPeaks?: boolean;
};

type ControlTableProps = {
    data: any[];
    displayedRows: any[];
};

const ControlTable = ({data, displayedRows}: ControlTableProps) => {
    const columns = data?.[0] ? Object.keys(data[0]?.other_categories) : [];
    return (
        <TableContainer sx={{border: '1px solid', borderColor: 'outline', borderRadius: '5px', maxHeight: 600, overflowY: 'auto'}}>
            <Table stickyHeader aria-label="control table">
                <TableHead>
                    <TableRow
                        sx={{
                            '& .MuiTableCell-root': {
                                bgcolor: 'surface-container-highest'
                            }
                        }}>
                        <TableCell align="left">SAMPLE NAME</TableCell>
                        {columns.map((c: string) => (
                            <TableCell key={c} align="left">
                                {c.toUpperCase()}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {displayedRows?.map((row: any) => (
                        <TableRow key={row.id} sx={{'& > *': {borderBottom: '0'}, bgcolor: 'on-primary'}} hover>
                            <TableCell align="left">{row?.name}</TableCell>
                            {columns.map((c: string) => (
                                <TableCell key={c} align="left">
                                    {row.other_categories[c]}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const SamplesTable = ({data, displayedRows, selectedSamples, handleSelectAll, handleSelectRow, filterPeaks = false}: SamplesTableProps) => {
    // TODO: Duplicates logic in useSampleSelection
    const rowsToSelect = displayedRows.filter(row => {
        let keep = true;
        if (filterPeaks) {
            keep = keep && row.peaks >= DBA_PEAK_THRESHOLD;
        }
        return keep;
    });
    const allSelected = rowsToSelect.every(row => selectedSamples.includes(row.id));
    return (
        <TableContainer sx={{border: '1px solid', borderColor: 'outline', borderRadius: '5px', maxHeight: 600, overflowY: 'auto'}}>
            <Table stickyHeader aria-label="collapsible table">
                <TableHead>
                    <TableRow
                        sx={{
                            '& .MuiTableCell-root': {
                                bgcolor: 'surface-container-highest'
                            }
                        }}>
                        <TableCell width="10px" />
                        <TableCell padding="checkbox">
                            <Checkbox
                                checked={allSelected && displayedRows.length > 0}
                                onChange={handleSelectAll}
                                disabled={isDemoExperiment(data?.[0]?.experiment_id)}
                            />
                        </TableCell>
                        <TableCell align="left">SAMPLE NAME</TableCell>
                        <TableCell align="left">STATUS</TableCell>
                        {filterPeaks && (
                            <TableCell align="right">
                                <Box display="inline-flex" alignItems="center" gap={0.5}>
                                    PEAKS
                                    <Tooltip title="These are the peaks computed for each sample based on chosen default analysis. A peak value of 0 indicates that no peaks were detected for the sample.">
                                        <InfoOutlinedIcon fontSize="small" color="action" />
                                    </Tooltip>
                                </Box>
                            </TableCell>
                        )}
                        {data?.[0] &&
                            Object.keys(data[0]?.other_categories)?.map((c: string) => (
                                <TableCell key={c} align="left">
                                    {c.toUpperCase()}
                                </TableCell>
                            ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {displayedRows?.map((row: any) => (
                        <SampleRow
                            key={row.id}
                            row={row}
                            selected={selectedSamples.includes(row.id)}
                            onSelect={() => handleSelectRow(row.id)}
                            expId={data[0].experiment_id}
                            showPeaks={filterPeaks}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

/**
 * Categorizes samples into three groups: Controls, IP with Control, and IP without Control
 */
const categorizeSamples = (data: any[]) => {
    const controlSamples = data?.filter((s: any) => s.other_categories?.SampleType === 'Input') || [];
    const ipControlSamples =
        data?.filter((s: any) => s.other_categories?.SampleType === 'IP' && s.other_categories?.ControlName !== 'NoControl') || [];
    const ipNoControlSamples =
        data?.filter((s: any) => s.other_categories?.SampleType === 'IP' && s.other_categories?.ControlName === 'NoControl') || [];

    return {
        controlSamples,
        ipControlSamples,
        ipNoControlSamples
    };
};

/**
 * Custom sorta hook to manage sample selection logic.
 *
 * When autoExpandSelection is true, selecting a sample selects all its
 * replicates. As we don't provide checkboxes for the input samples, they are
 * not explicitly selected here, to avoid confusion to the user. However, they
 * will still be added during final expansion at submission.
 */
const useSampleSelection = (formData: any, setFormData: (updater: any) => void, allSamples: any[], autoExpandSelection: boolean) => {
    const isDifferentialBinding = formData.type === 'Differential Binding';

    const handleSelectRow = (id: string) => {
        setFormData((prev: {name: string; sample_ids: string[]}) => {
            if (prev.sample_ids.includes(id)) {
                // Deselecting
                if (autoExpandSelection) {
                    // Remove entire replicate group
                    const expandedIds = expandSampleSelection([id], allSamples);
                    return {
                        ...prev,
                        sample_ids: prev.sample_ids.filter((item: string) => !expandedIds.includes(item))
                    };
                } else {
                    // Remove only the specific sample
                    return {
                        ...prev,
                        sample_ids: prev.sample_ids.filter((item: string) => item !== id)
                    };
                }
            } else {
                // Selecting
                if (autoExpandSelection) {
                    // Add entire replicate group
                    const expandedIds = expandSampleSelection([id], allSamples);
                    const validIds = expandedIds.filter(expandedId => {
                        const sample = allSamples.find(s => s.id === expandedId);
                        if (!sample) return false;
                        // Filter out Input (control) samples
                        if (sample.other_categories?.SampleType === 'Input') return false;
                        // Filter out samples with peaks < DBA_PEAK_THRESHOLD if differential binding
                        if (isDifferentialBinding && sample.peaks < DBA_PEAK_THRESHOLD) return false;
                        return true;
                    });
                    return {
                        ...prev,
                        sample_ids: Array.from(new Set([...prev.sample_ids, ...validIds]))
                    };
                } else {
                    // Add only the specific sample
                    return {
                        ...prev,
                        sample_ids: [...prev.sample_ids, id]
                    };
                }
            }
        });
    };

    const handleSelectAll = (visibleRows: any[]) => {
        const visibleIds = visibleRows
            .filter(r => {
                let keep = true;
                if (isDifferentialBinding) {
                    keep = keep && r.peaks >= DBA_PEAK_THRESHOLD;
                }
                return keep;
            })
            .map(r => r.id);

        const allSelected = visibleIds.every(id => formData.sample_ids.includes(id));

        setFormData((prev: any) => ({
            ...prev,
            sample_ids: allSelected
                ? prev.sample_ids.filter((id: string) => !visibleIds.includes(id))
                : Array.from(new Set([...prev.sample_ids, ...visibleIds]))
        }));
    };

    return {
        selectedSamples: formData.sample_ids,
        handleSelectRow,
        handleSelectAll,
        isDifferentialBinding
    };
};

interface ExpandableTableChipProps {
    /** Form data containing analysis configuration and selected sample IDs */
    formData: any;
    /** Function to update form data */
    setFormData: any;
    /** Callback to enable/disable the continue button based on validation */
    handleNext: any;
    /** Array of sample objects to display in the table */
    data: any[];
    /** Error message to display, if any */
    error: any;
    /** Function to set error messages */
    setError: any;
    /**
     * When true, selecting a sample automatically selects all its replicates and controls.
     * Deselecting removes the entire replicate group. Only applies to individual row selection,
     * not "Select All". Final expansion still happens at submission.
     * @default false
     */
    autoExpandSelection?: boolean;
    defaultChIPSampleGroups?: DefaultChIPSampleGroup[];
    loadingDefaultChIPSampleGroups?: boolean;
}

const ExpandableTableChip = ({
    formData,
    setFormData,
    handleNext,
    data,
    error,
    setError,
    autoExpandSelection = false
}: ExpandableTableChipProps) => {
    const navigate = useNavigate();
    const {user} = useTrovoConfig();
    const [searchTerm, setSearchTerm] = useState('');

    const {selectedSamples, handleSelectRow, handleSelectAll, isDifferentialBinding} = useSampleSelection(
        formData,
        setFormData,
        data,
        autoExpandSelection
    );

    const {controlSamples, ipControlSamples, ipNoControlSamples} = useMemo(() => categorizeSamples(data), [data]);
    const ipCount = ipControlSamples.length + ipNoControlSamples.length;
    const [filteredControlSamples, setFilteredControlSamples] = useState(controlSamples);
    const [filteredIpControl, setFilteredIpControl] = useState(ipControlSamples);
    const [filteredIpNoControl, setFilteredIpNoControl] = useState(ipNoControlSamples);
    const minSamplesRequired = isDifferentialBinding ? 4 : 1;
    // tracks minimum number of samples user will need to pick before moving on to next step
    useEffect(() => {
        const numOfSamplesNeeded = minSamplesRequired - selectedSamples.length;
        selectedSamples.length && selectedSamples.length < minSamplesRequired && !user.cb_item_price_id?.includes('demo')
            ? setError(
                  `Please select at least ${numOfSamplesNeeded} more ${numOfSamplesNeeded === 1 ? 'sample' : 'samples'} before proceeding.`
              )
            : setError('');
    }, [selectedSamples]);

    useEffect(() => {
        handleNext(!error && selectedSamples.length >= minSamplesRequired);
    }, [selectedSamples, error]);

    useEffect(() => {
        setFilteredControlSamples(controlSamples.filter((r: any) => r?.name?.toString().toLowerCase().includes(searchTerm.toLowerCase())));
        setFilteredIpControl(ipControlSamples.filter((r: any) => r?.name?.toString().toLowerCase().includes(searchTerm.toLowerCase())));
        setFilteredIpNoControl(ipNoControlSamples.filter((r: any) => r?.name?.toString().toLowerCase().includes(searchTerm.toLowerCase())));
    }, [searchTerm, controlSamples, ipControlSamples, ipNoControlSamples]);

    return (
        <>
            <ActionBar>
                <ActionItemStyled
                    sx={{cursor: 'pointer', bgcolor: 'surface-container-lowest'}}
                    onClick={() => navigate(`/samples/${data?.[0]?.experiment_id}`)}>
                    <div>
                        <ActionTitleStyled>Manage Samples</ActionTitleStyled>
                        <ActionTextStyled>Edit or review sample statuses.</ActionTextStyled>
                    </div>
                    <ArrowRight sx={{color: 'black'}} />
                </ActionItemStyled>
            </ActionBar>

            <Box sx={{bgcolor: 'surface-container-lowest', padding: '2%', borderRadius: '10px'}}>
                <FiltersContainer>
                    <FiltersWrapper>
                        <Search>
                            <SearchIconWrapper>
                                <SearchIcon />
                            </SearchIconWrapper>
                            <StyledInputBase
                                placeholder="Search…"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                                inputProps={{'aria-label': 'search'}}
                            />
                        </Search>
                    </FiltersWrapper>
                    <SelectedRowsTextStyled>
                        <b>{selectedSamples?.length}</b> of <b>{ipCount}</b> samples selected for analysis
                    </SelectedRowsTextStyled>
                </FiltersContainer>

                {controlSamples.length > 0 && (
                    <>
                        <Typography variant="h4" sx={{my: 1}}>
                            Controls
                        </Typography>
                        <ControlTable data={controlSamples} displayedRows={filteredControlSamples} />
                    </>
                )}
                {ipControlSamples.length > 0 && (
                    <>
                        <Typography variant="h4" sx={{my: 1}}>
                            IP with Control
                        </Typography>
                        <SamplesTable
                            data={ipControlSamples}
                            displayedRows={filteredIpControl}
                            selectedSamples={selectedSamples}
                            handleSelectAll={() => handleSelectAll(filteredIpControl)}
                            handleSelectRow={handleSelectRow}
                            filterPeaks={isDifferentialBinding}
                        />
                    </>
                )}
                {ipNoControlSamples.length > 0 && (
                    <>
                        <Typography variant="h4" sx={{my: 1}}>
                            IP without Control
                        </Typography>
                        <SamplesTable
                            data={ipNoControlSamples}
                            displayedRows={filteredIpNoControl}
                            selectedSamples={selectedSamples}
                            handleSelectAll={() => handleSelectAll(filteredIpNoControl)}
                            handleSelectRow={handleSelectRow}
                            filterPeaks={isDifferentialBinding}
                        />
                    </>
                )}
            </Box>
        </>
    );
};

const AnalysisSampleSelector = (props: ExpandableTableChipProps) =>
    props.formData.type === 'Functional Enrichment' ? (
        <SampleGroupSelector
            formData={props.formData}
            setFormData={props.setFormData}
            handleNext={props.handleNext}
            data={props.data}
            error={props.error}
            defaultChIPSampleGroups={props.defaultChIPSampleGroups ?? []}
            loadingDefaultChIPSampleGroups={props.loadingDefaultChIPSampleGroups ?? false}
        />
    ) : (
        <ExpandableTableChip {...props} />
    );

export default AnalysisSampleSelector;
